import { NotePreviewTooltipContent } from '@/components/notes/note-preview-tooltip-content';
import { cn } from '@/lib/utils';
import type { NoteThread } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { Tooltip } from '@pms-digital/design-system';

/** Marks the controls that open or write notes: clicking one must not count as « outside » for the bubble. */
export const NOTE_TRIGGER_ATTRIBUTE = 'data-note-trigger';

/** The cell or header a control sits in: where the bubble anchors. */
export function noteAnchorOf(control: HTMLElement): HTMLElement {
  return control.closest<HTMLElement>('td, th') ?? control;
}

export interface NoteMarkerProps {
  /** Open discussions on the spot (visible to the viewer); the marker is only drawn for one or more. */
  count: number;
  /** The spot, as the accessible name words it: « Palier Barre des minimas ». */
  targetLabel: string;
  /** The latest open discussion, previewed on hover and focus. */
  latest?: NoteThread | undefined;
  /** Receives the cell or header the marker sits in. */
  onOpen: (anchor: HTMLElement) => void;
  /** The clock of the relative date; tests pin it. */
  now?: Date;
  className?: string;
}

/**
 * The marker of the grid (planche 10): a gray chat bubble followed by the
 * number of open discussions on the spot. A real button: a click opens the
 * notes of the spot, hover and keyboard focus preview the latest discussion
 * (author, date, number of messages, the first two lines of its text).
 */
export function NoteMarker({
  count,
  targetLabel,
  latest,
  onOpen,
  now,
  className,
}: NoteMarkerProps) {
  const label = `${count} ${count === 1 ? 'note ouverte' : 'notes ouvertes'} sur ${targetLabel}`;
  const button = (
    <button
      type="button"
      {...{ [NOTE_TRIGGER_ATTRIBUTE]: '' }}
      aria-label={label}
      onClick={(event) => {
        // The cell or header behind must not also select or start an edit.
        event.stopPropagation();
        onOpen(noteAnchorOf(event.currentTarget));
      }}
      // The cell handles Enter and Space itself, which would swallow the click.
      onKeyDown={(event) => event.stopPropagation()}
      className={cn(
        'inline-flex shrink-0 items-center gap-0 rounded px-1 py-0 font-normal text-gray-600 text-micro leading-none hover:bg-gray-200 hover:text-bleu-nuit focus-visible:outline-2 focus-visible:outline-bleu-nuit',
        className,
      )}
    >
      <Icon name="chat_bubble" size={14} />
      <span aria-hidden="true">{count}</span>
    </button>
  );

  if (!latest) return button;
  return (
    <Tooltip
      side="bottom"
      content={
        <div data-testid="note-marker-preview">
          <NotePreviewTooltipContent thread={latest} {...(now ? { now } : {})} />
        </div>
      }
    >
      {button}
    </Tooltip>
  );
}
